import { AuditTrailAction } from '../../audit/domain/auditTrailAction';
import { DashboardComponentType } from '../../dashboards/domain/dashboardComponentType';
import { LogSorterColumnPlacement } from '../../logSorter/domain/logSorterBinLayout';
import { LogSorterNumberSequence } from '../../logSorter/domain/logSorterNumberSequence';
import { Anchor } from '../../map/domain/anchor';
import { ProductionOrderState } from '../../productionOrders/domain/productionOrderState';
import { ProductType } from '../../products/domain/productType';
import { ReportType } from '../../reports/domain/reportType';
import { ShapeMode } from '../../shapes/domain/shapeMode';
import { StorageAreaStatus } from '../../storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from '../../storageAreas/domain/storageAreaType';
import { VehicleType } from '../../vehicles/domain/vehicleType';
import { VehicleSymbol } from '../../vehicles/domain/vehicleSymbol';
import { KeyboardActions } from '../../vehicles/domain/keyboardActions';
import type { Translations } from '../domain/translations';
import { Unit } from '../../common/domain/bulkUnits';

export const german: Translations = {
  generic: {
    active: 'Aktiv',
    language: 'Sprache',
    yes: 'Ja',
    no: 'Nein',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    id: 'ID',
    name: 'Name',
    description: 'Beschreibung',
    type: 'Typ',
    code: 'Code',
    quality: 'Qualität',
    columns: 'Spalten',
    color: 'Farbe',
    capacity: 'Kapazität',
    loadingTableData: 'Lädt...',
    search: 'Suchen',
    generalErrorMsg: 'Ups! Etwas ist schiefgelaufen',
    signIn: 'Anmelden',
    signOut: 'Abmelden',
    width: 'Breite',
    length: 'Länge',
    height: 'Höhe',
    login: 'Anmelden',
    logout: 'Abmelden',
    next: 'Weiter',
    back: 'Zurück',
    abort: 'Abbrechen',
    close: 'Schließen',
    searchBy: 'Suchen nach',
    clear: 'Löschen',
    error: 'Fehler',
    success: 'Erfolg',
    updated: 'Aktualisiert',
    added: 'Hinzugefügt',
    taskCompleted: 'Aufgabe abgeschlossen',
    created: 'Erstellt',
    deleted: 'Gelöscht',
    toggle: 'Umschalten',
    status: 'Status',
    position: 'Position',
    direction: 'Richtung',
    anchor: 'Anker',
    product: 'Produkt',
    vehicle: 'Fahrzeug',
    map: 'Karte',
    settings: 'Einstellungen',
    geofence: 'Geofence',
    geofenceTop: 'Geofence oben',
    geofenceBottom: 'Geofence unten',
    geofenceLeft: 'Geofence links',
    geofenceRight: 'Geofence rechts',
    storageArea: 'Lagerbereich',
    inventory: 'Inventar',
    noAvailableOptions: 'Keine verfügbaren Optionen',
    edit: 'Bearbeiten',
    select: 'Auswählen',
    default: 'Standard',
    mirrored: 'Gespiegelt',
    add: 'Hinzufügen',
    top: 'Oben',
    bottom: 'Unten',
    left: 'Links',
    right: 'Rechts',
    longitude: 'Längengrad',
    latitude: 'Breitengrad',
    shape: 'Form',
    inside: 'Innen',
    outside: 'Außen',
    logSorter: 'Stammsortierer',
    confirm: 'Bestätigen',
    on: 'An',
    off: 'Aus',
    circle: 'Kreis',
    polygon: 'Polygon',
    size: 'Größe',
    rotate: 'Drehen',
    freeform: 'Freihand',
    rectangle: 'Rechteck',
    empty: 'Leer',
    discard: 'Verwerfen',
    audit: 'Audit',
    export: 'Exportieren',
    import: 'Importieren',
    take: 'Nehmen',
    ok: 'OK',
    from: 'Von',
    to: 'Nach',
    move: 'Verschieben',
    coil: 'Coil',
    swap: 'Tauschen',
    copy: 'Kopieren',
    pick: 'Aufnehmen',
    unknown: 'Unbekannt',
    enabled: 'Aktiviert',
    note: 'Notiz',
    groups: 'Gruppen',
    productGroup: 'Produktgruppe',
    products: 'Produkte',
    numberOfLogs: 'Anzahl der Stämme',
    balance: 'Saldo',
    fifo: 'FIFO',
    volume: 'Volumen',
    numberOfProductsInStorage: 'Anzahl Produkte',
    storageAreas: 'Lagerbereiche',
    showMore: 'Mehr anzeigen',
    preview: 'Vorschau',
    selectColumns: 'Spalten auswählen',
    locked: 'Gesperrt',
    unlocked: 'Entsperrt',
    keybindings: 'Tastenkombinationen',
    productionOrder: 'Produktionsauftrag',
    resource: 'Ressource',
    priority: 'Priorität',
    amount: 'Menge',
    newVersion: 'Neue Version von SILog',
    important: 'Wichtig',
    noData: 'Keine Daten',
    total: 'Gesamt',
    calculate: 'Berechnen',
    sorted: 'Sortiert',
    unsorted: 'Unsortiert',
    unit: 'Einheit',
  },
  application: {
    exit: 'Beenden',
    exitSilog: 'SILog beenden',
    confirmExit: 'Sind Sie sicher, dass Sie SILog beenden möchten?',
    openSettings: 'App-Einstellungen öffnen',
  },
  sites: {
    actions: {
      fetchMany: {
        title: 'Standorte werden abgerufen',
        error: 'Fehler beim Abrufen der Standorte',
      },
    },
    notFound: 'Standort nicht gefunden',
  },
  sideMenu: {
    home: 'Startseite',
    map: 'Karte',
    vehicles: 'Fahrzeuge',
    products: 'Produkte',
    styles: 'Stile',
    coils: 'Coils',
    systemsettings: 'Systemeinstellungen',
    dashboards: 'Dashboards',
    productionOrders: 'Produktionsaufträge',
    reports: 'Berichte',
  },
  vehicleType(type: VehicleType): string {
    switch (type) {
      case VehicleType.Undefined:
        return 'Undefiniert';
      case VehicleType.Automatic:
        return 'Automatisch';
      case VehicleType.LogHandler:
        return 'Holzhandler';
      case VehicleType.Truck:
        return 'LKW';
      case VehicleType.CoilForklift:
        return 'Coil-Stapler';
      case VehicleType.BulkHandler:
        return 'Massenabwickler';
    }
  },
  vehicleSymbol(symbol: VehicleSymbol): string {
    switch (symbol) {
      case VehicleSymbol.Undefined:
        return 'Pfeil (Standard)';
      case VehicleSymbol.LogStacker:
        return 'Holzstapler';
      case VehicleSymbol.WheelLoaderFrontGrapple:
        return 'Radlader - Frontgreifer';
      case VehicleSymbol.WheelLoaderHighGrapple:
        return 'Radlader - Hochgreifer';
      case VehicleSymbol.TimberCrane:
        return 'Holzkran';
      case VehicleSymbol.CoilForklift:
        return 'Coil-Stapler';
    }
  },
  productType(type: ProductType): string {
    switch (type) {
      case ProductType.Log:
        return 'Stamm';
      case ProductType.Undefined:
        return 'Undefiniert';
      case ProductType.Coil:
        return 'Coil';
      case ProductType.Bulk:
        return 'Schüttgut';
    }
  },
  products: {
    actions: {
      create: {
        title: 'Produkt wird erstellt',
        error: 'Fehler beim Erstellen des Produkts',
      },
      update: {
        title: 'Produkt wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Produkts',
      },
      delete: {
        title: 'Produkt wird gelöscht',
        error: 'Fehler beim Löschen des Produkts',
        statusCodes: {
          409: 'Produkt ist noch in einem Lagerbereich in Verwendung',
        },
      },
      fetch: {
        title: 'Produkt wird abgerufen',
        error: 'Fehler beim Abrufen des Produkts',
      },
      fetchMany: {
        title: 'Produkte werden abgerufen',
        error: 'Fehler beim Abrufen der Produkte',
      },
      editLogYardProducts(number: number) {
        return `${number} Holzplatz-Produkte bearbeiten`;
      },
      editCoilProducts(number: number) {
        return `${number} Coil-Produkte bearbeiten`;
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Produkt ${name} löschen`;
      },
      description:
        'Sind Sie sicher, dass Sie dieses Produkt löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Produkt löschen',
    },

    products: 'Produkte',
    addNewProduct: 'Neues Produkt hinzufügen',
    editProduct: 'Produkt bearbeiten',
    editMultipleProducts(number: number) {
      return `${number} Produkte bearbeiten`;
    },
    fetchDataError: 'Fehler beim Abrufen der Produktdaten',
    addNewProductError: 'Produkt konnte nicht erstellt werden',
    editProductError: 'Produkt konnte nicht aktualisiert werden',
    deleteProductError: 'Produkt konnte nicht gelöscht werden:',
    noProductsFound: 'Keine Produkte gefunden.',
    loadingProductData: 'Produktdaten werden geladen..',
    logsPerSquareMeter: 'Stämme/m²',
    notFound: 'Produkt nicht gefunden',
    couldNotFetchProduct: 'Produkt konnte nicht abgerufen werden!',
    logData: {
      logData: 'Stammdaten',
      logsPerSquareMeter: 'Stämme pro m²',
      minDiameter: 'Min. Durchmesser',
      maxDiameter: 'Max. Durchmesser',
      maxLength: 'Max. Länge',
      minLength: 'Min. Länge',
      averageCylinderVolume: 'Durchschnittliches Zylindervolumen',
      averagePhysicalVolume: 'Durchschnittliches physisches Volumen',
      isSorted: 'Ist sortiert',
      species: 'Holzschlacke',
      logSorterName: 'Name des Stammsortierers',
      sawLineName: 'Name der Sägelinie',
      logsPerSquareMeterDescription:
        'Wird verwendet, um zu schätzen, wie viele Stämme ein Fahrzeug von einem Lagerbereich aufnehmen kann',
    },
    coilData: {
      title: 'Coil',
      diameter: 'Durchmesser',
      depth: 'Tiefe',
    },
    bulkData: {
      title: 'Schüttgut',
      unit: 'Einheit',
      density: 'Dichte',
    },
  },
  productGroups: {
    actions: {
      create: {
        title: 'Produktgruppe wird erstellt',
        error: 'Fehler beim Erstellen der Produktgruppe',
      },
      update: {
        title: 'Produktgruppe wird aktualisiert',
        error: 'Fehler beim Aktualisieren der Produktgruppe',
      },
      delete: {
        title: 'Produktgruppe wird gelöscht',
        error: 'Fehler beim Löschen der Produktgruppe',
      },
      fetch: {
        title: 'Produktgruppe wird abgerufen',
        error: 'Fehler beim Abrufen der Produktgruppe',
      },
      fetchMany: {
        title: 'Produktgruppen werden abgerufen',
        error: 'Fehler beim Abrufen der Produktgruppen',
      },
    },
    listPage: {
      title: 'Produktgruppen',
      empty: 'Keine Produktgruppen',
      loading: 'Produktgruppen werden geladen...',
      addNew: 'Neue Gruppe hinzufügen',
      edit: 'Gruppe bearbeiten',
    },
    deleteDialog: {
      header(name: string) {
        return `Produktgruppe ${name} löschen`;
      },
      description:
        'Sind Sie sicher, dass Sie diese Produktgruppe löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Produktgruppe löschen',
    },
  },
  vehicles: {
    actions: {
      create: {
        title: 'Fahrzeug wird erstellt',
        error: 'Fehler beim Erstellen des Fahrzeugs',
      },
      update: {
        title: 'Fahrzeug wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Fahrzeugs',
      },
      delete: {
        title: 'Fahrzeug wird gelöscht',
        error: 'Fehler beim Löschen des Fahrzeugs',
      },
      fetch: {
        title: 'Fahrzeug wird abgerufen',
        error: 'Fehler beim Abrufen des Fahrzeugs',
      },
      fetchMany: {
        title: 'Fahrzeuge werden abgerufen',
        error: 'Fehler beim Abrufen der Fahrzeuge',
      },
      startReservation: {
        title: 'Fahrzeugreservierung wird gestartet',
        error: 'Fehler beim Reservieren des Fahrzeugs',
      },
      endReservation: {
        title: 'Fahrzeugreservierung wird beendet',
        error: 'Fehler beim Beenden der Fahrzeugreservierung',
      },
      updateKeybinds: {
        title: 'Fahrzeug-Tastenkombinationen werden aktualisiert',
        error: 'Fehler beim Aktualisieren der Fahrzeug-Tastenkombinationen',
      },
    },
    vehicles: 'Fahrzeuge',
    noVehiclesFound: 'Keine Fahrzeuge gefunden.',
    loadingVehicleData: 'Fahrzeugdaten werden geladen..',
    addNewVehicle: 'Neues Fahrzeug hinzufügen',
    editVehicle: 'Fahrzeug bearbeiten',
    confirmDeleteMsg:
      'Sind Sie sicher, dass Sie dieses Fahrzeug löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
    deleteBtnTxt: 'Ja, Fahrzeug löschen',
    fetchDataError: 'Fehler beim Abrufen der Fahrzeugdaten',
    editVehicleSuccess: 'Bearbeitet',
    addNewVehicleSuccess: 'Erfolg: hinzugefügt',
    deleteVehicleSuccess: 'Erfolg: gelöscht',
    deleteVehicleError: 'Fahrzeug konnte nicht gelöscht werden:',
    nameMustBeUnique: 'Name muss eindeutig sein',
    symbol: 'Symbol',
    symbolPreview: 'Vorschau',
    capacityDescription:
      'Wird verwendet, um zu schätzen, wie viele Stämme dieses Fahrzeug von einem Lagerbereich aufnehmen kann. Es wird in Kombination mit den "Stämmen pro Quadratmeter" des Produkts verwendet.',
    deleteVehicle(name: string) {
      return `Fahrzeug ${name} löschen`;
    },
    inUseDialog: {
      header: 'Fahrzeug wird verwendet!',
    },
    reservationEnded: 'Jemand anderes hat Ihr Fahrzeug übernommen!',
    reservationEndedDescription: (userId: string) =>
      `Ihr Fahrzeug wurde von ${userId.split('|')[0]} übernommen`,
    automaticLogYardMoves: 'Automatische Holzplatzbewegungen',
    automaticLogYardMovesLogSorterBinFull: 'Wenn Stammsortierer-Fach voll ist',
    automaticLogYardMovesLogSorterBinChangeProduct: 'Wenn Stammsortierer-Fach Produkt wechselt',
  },
  addLogSorterDialog: {
    addLogSorter: 'Stammsortierer hinzufügen',
    editLogSorter: 'Stammsortierer bearbeiten',
    name: 'Name',
    bin: 'Fach',
    bins: 'Fächer',
    identifiers: 'ID',
    logSorter: 'Stammsortierer',
    selectLogSorter: 'Stammsortierer auswählen',
    startPositionX: 'Startposition X',
    startPositionY: 'Startposition Y',
    columns: 'Spalten',
    numberOfBins: 'Anzahl der Fächer',
    numberSequence: 'Zahlenfolge',
    rotation: 'Rotation',
    direction: 'Richtung',
    binWidth: 'Fachbreite',
    binLength: 'Fachhöhe',
    distanceX: 'Abstand zwischen X',
    distanceY: 'Abstand zwischen Y',
    geoFenceOffsetTop: 'Geofence oben',
    geoFenceOffsetBottom: 'Geofence unten',
    geoFenceOffsetLeft: 'Geofence links',
    geoFenceOffsetRight: 'Geofence rechts',
    geoFenceOffsetInside: 'Geofence innen',
    geoFenceOffsetOutside: 'Geofence außen',
    addLogWhenSorted: 'Stamm hinzufügen, wenn sortiert',
    intake: 'Eingang',
    intakes: 'Eingänge',
    addNewIntake: 'Neuen Eingang hinzufügen',
    deleteIntake: 'Eingang löschen',
    savingStorageAreas: 'Lagerbereiche werden gespeichert',
    savingShapes: 'Formen werden gespeichert',
    savingLogSorter: 'Stammsortierer wird gespeichert',
    success: 'Erfolg!',
    savingBins: 'Fächer werden gespeichert',
    savingIntakes: 'Eingänge werden gespeichert',
    one: '1',
    twoLeft: '2L',
    twoRight: '2R',
  },
  dialogs: {
    storageArea: {
      addStorageArea: 'Lagerbereich erstellen',
      editStorageArea: 'Lagerbereich bearbeiten',
      storageArea: 'Lagerbereich',
      automaticProductChange: 'Automatischer Produktwechsel',
      allowMultipleProducts: 'Mehrere Produkte zulassen',
      allowedProducts: 'Zugelassene Produkte',
      selectAllowedProducts: 'Zugelassene Produkte auswählen',
      selectStorageAreaType: 'Lagerbereichstyp auswählen',
      selectStorageAreaStatus: 'Lagerbereichsstatus auswählen',
      confirmDeleteHeader: 'Lagerbereich löschen',
      confirmDeleteMessage: (name: string) =>
        `Sind Sie sicher, dass Sie den Lagerbereich ${name} löschen möchten?`,
      deleteError: 'Lagerbereich konnte nicht gelöscht werden',
      updateError: 'Lagerbereich konnte nicht aktualisiert werden',
      createError: 'Lagerbereich konnte nicht erstellt werden',
      editShape: 'Form bearbeiten',
      sideArea: 'Seitenfläche',
      sideAreaDescription:
        'Wird verwendet, um die Menge der Stämme zu schätzen, die in diesen Lagerbereich passen',
    },
    storageAreaSelect: {
      title: 'Lagerbereichsinformationen',
    },
    inventory: {
      title: 'Inventar',
      selectProduct: 'Produkt auswählen',
      currentAmount: 'Aktuelle Menge:',
      setNewAmount: 'Neue Menge festlegen',
      storageAreaNotFound: 'Lagerbereich nicht gefunden!',
      confirmHeader: (name: string) => `Inventar ${name}`,
      confirmMessage: (name: string, product: string) =>
        `Sind Sie sicher, dass Sie ${product} zu ${name} hinzufügen möchten? Mehrere Produkte zulassen ist deaktiviert.`,
    },
    confirmEmpty: {
      message:
        'Es befindet sich noch Inhalt im Lagerbereich. Sind Sie sicher, dass Sie ihn leeren möchten? Der verbleibende Inhalt wird gelöscht.',
      title: (name: string) => `Lagerbereich ${name} leeren`,
      willBeDeleted: 'wird gelöscht',
    },
    confirmAllowMultipleProducts: {
      message: (storageAreaName: string, product: string) =>
        `Mehrere Produkte zulassen ist deaktiviert. Sind Sie sicher, dass Sie "${product}" in ${storageAreaName} platzieren möchten?`,
      title: 'Warnung',
    },
    confirmPlaceNotAllowedProductInLogYard: {
      message: (storageAreaName: string, products: string) =>
        `Das Produkt "${products}" ist in ${storageAreaName} nicht zugelassen. Sind Sie sicher, dass Sie es hier platzieren möchten?`,
      title: 'Warnung',
    },
    moveLogYardContent: {
      pickTab: 'Aufnehmen von',
      pickButton: 'Aufnehmen',
      placeTab: 'Platzieren in',
      placeButton: 'Platzieren',
      pickAll: 'Leeren',
    },
    selectVehicle: {
      title: 'Fahrzeug auswählen',
      message: 'Bitte wählen Sie das Fahrzeug aus, das Sie verwenden',
    },
    vehicleInventory: {
      vehicleInventory: 'Fahrzeuginventar',
      vehicleLoad: 'Aktuelle Fahrzeugladung',
      leaveTitle: 'Ablegen in',
      leaveButton: 'Ablegen',
      moving: 'Verschieben',
    },
    search: {
      searchForProduct: 'Nach einem Produkt suchen',
      selectAProduct: 'Ein Produkt auswählen',
    },
    mapView: {
      title: 'Neue Kartenansicht hinzufügen',
      enterName: 'Namen eingeben',
    },
    selectLogSorter: {
      title: 'Stammsortierer auswählen',
    },
    productionOrder: {
      editTitle: 'Produktionsauftrag auswählen',
      selectProductionOrder: 'Produktionsauftrag auswählen',
    },
  },
  error: {
    required: 'Erforderlich.',
    invalidValue: 'Ungültiger Wert.',
    nameAlreadyExist: 'Name existiert bereits.',
    nameIsRequired: 'Ein Name ist erforderlich.',
    maxLength: (limit: number) => `Max. Länge ${limit}`,
    forbidden: 'Verboten',
    notFound: 'Nicht gefunden',
    conflict: 'Konflikt',
    network: 'Server konnte nicht erreicht werden',
    invalidDensity: 'Die Dichte muss mindestens 0,1 betragen.',
  },
  enums: {
    storageAreaStatus(status: StorageAreaStatus): string {
      switch (status) {
        case StorageAreaStatus.Empty:
          return 'Leer';
        case StorageAreaStatus.InUse:
          return 'In Benutzung';
        case StorageAreaStatus.Full:
          return 'Voll';
        case StorageAreaStatus.Undefined:
          return 'Undefiniert';
      }
    },
    storageAreaType(type: StorageAreaType): string {
      switch (type) {
        case StorageAreaType.Producer:
          return 'Produzent';
        case StorageAreaType.Consumer:
          return 'Verbraucher';
        case StorageAreaType.Vehicle:
          return 'Fahrzeug';
        case StorageAreaType.LogSorterIntake:
          return 'Stammsortierer-Eingang';
        case StorageAreaType.SawLineIntake:
          return 'Sägelinien-Eingang';
        case StorageAreaType.LogSorterBin:
          return 'Stammsortierer-Fach';
        case StorageAreaType.SortedPile:
          return 'Sortierter Stapel';
        case StorageAreaType.UnsortedPile:
          return 'Unsortierter Stapel';
        case StorageAreaType.Truck:
          return 'LKW';
        case StorageAreaType.Train:
          return 'Zug';
        case StorageAreaType.Undefined:
          return 'Undefiniert';
        case StorageAreaType.CoilStack:
          return 'Coil-Stapel';
        case StorageAreaType.Bulk:
          return 'Grob;';
      }
    },
    shapeMode(type: ShapeMode): string {
      switch (type) {
        case ShapeMode.Rectangle:
          return 'Rechteck';
        case ShapeMode.Circle:
          return 'Kreis';
        case ShapeMode.Polygon:
          return 'Polygon';
      }
    },
    logSorterNumberSequence(value: LogSorterNumberSequence): string {
      switch (value) {
        case LogSorterNumberSequence.Sequential:
          return 'Sequentiell';
        case LogSorterNumberSequence.SequentialMirrored:
          return 'Sequentiell gespiegelt';
        case LogSorterNumberSequence.Alternating:
          return 'Abwechselnd';
        case LogSorterNumberSequence.AlternatingMirrored:
          return 'Abwechselnd gespiegelt';
      }
    },
    logSorterBinLayout(value: LogSorterColumnPlacement): string {
      switch (value) {
        case LogSorterColumnPlacement.Left:
          return 'Links';
        case LogSorterColumnPlacement.Right:
          return 'Rechts';
      }
    },
    anchor(value: Anchor): string {
      switch (value) {
        case Anchor.Center:
          return 'Mitte';
        case Anchor.BottomCenter:
          return 'Unten Mitte';
        case Anchor.BottomLeft:
          return 'Unten Links';
        case Anchor.BottomRight:
          return 'Unten Rechts';
        case Anchor.TopCenter:
          return 'Oben Mitte';
        case Anchor.TopLeft:
          return 'Oben Links';
        case Anchor.TopRight:
          return 'Oben Rechts';
      }
    },
    auditTrailAction(action: AuditTrailAction): string {
      switch (action) {
        case AuditTrailAction.InventoryApiExecute:
          return 'Inventar';
        case AuditTrailAction.InventoryApiUndo:
          return 'Inventar rückgängig machen';
        case AuditTrailAction.LogSorterApiCreate:
          return 'Stammsortierer erstellen';
        case AuditTrailAction.LogSorterApiDelete:
          return 'Stammsortierer löschen';
        case AuditTrailAction.LogSorterApiUpdate:
          return 'Stammsortierer aktualisieren';
        case AuditTrailAction.MapStyleApiCreate:
          return 'Kartenstil erstellen';
        case AuditTrailAction.MapStyleApiDelete:
          return 'Kartenstil löschen';
        case AuditTrailAction.MapStyleApiUpdate:
          return 'Kartenstil aktualisieren';
        case AuditTrailAction.MoveApiUndo:
          return 'Verschiebung rückgängig machen';
        case AuditTrailAction.MoveApi:
          return 'Verschieben';
        case AuditTrailAction.ProductApiCreate:
          return 'Produkt erstellen';
        case AuditTrailAction.ProductApiDelete:
          return 'Produkt löschen';
        case AuditTrailAction.ProductApiUpdate:
          return 'Produkt aktualisieren';
        case AuditTrailAction.ShapeApiCreate:
          return 'Form erstellen';
        case AuditTrailAction.ShapeApiDelete:
          return 'Form löschen';
        case AuditTrailAction.ShapeApiUpdate:
          return 'Form aktualisieren';
        case AuditTrailAction.ShapeApiUndoDelete:
          return 'Form-Aktualisierung rückgängig machen';
        case AuditTrailAction.StorageAreaApiCreate:
          return 'Lagerbereich erstellen';
        case AuditTrailAction.StorageAreaApiDelete:
          return 'Lagerbereich löschen';
        case AuditTrailAction.StorageAreaApiUndoDelete:
          return 'Lagerbereich-Löschung rückgängig machen';
        case AuditTrailAction.StorageAreaApiUpdate:
          return 'Lagerbereich aktualisieren';
        case AuditTrailAction.SyncMove:
          return 'Verschieben';
        case AuditTrailAction.VehicleApiCreate:
          return 'Fahrzeug erstellen';
        case AuditTrailAction.VehicleApiDelete:
          return 'Fahrzeug löschen';
        case AuditTrailAction.VehicleApiUpdate:
          return 'Fahrzeug aktualisieren';
        case AuditTrailAction.Undefined:
          return 'Undefiniert';
        case AuditTrailAction.SyncInventory:
          return 'Inventar synchronisieren';
        case AuditTrailAction.SyncProduct:
          return 'Produkt synchronisieren';
        case AuditTrailAction.SyncShape:
          return 'Form synchronisieren';
        case AuditTrailAction.SyncStorageArea:
          return 'Lagerbereich synchronisieren';
        case AuditTrailAction.SyncUndoInventory:
          return 'Inventar-Rückgängigmachung synchronisieren';
        case AuditTrailAction.SyncUndoMove:
          return 'Verschiebungs-Rückgängigmachung synchronisieren';
        case AuditTrailAction.SyncUndoStorageArea:
          return 'Lagerbereich-Rückgängigmachung synchronisieren';
        case AuditTrailAction.CoilApiCreate:
          return 'Coil erstellen';
        case AuditTrailAction.CoilApiUpdate:
          return 'Coil aktualisieren';
        case AuditTrailAction.CoilApiDelete:
          return 'Coil löschen';
        case AuditTrailAction.FeatureFlagApi:
          return 'Feature-Flags aktualisieren';
        case AuditTrailAction.SettingUpdate:
          return 'Einstellungen aktualisieren';
        case AuditTrailAction.SyncFeatureFlag:
          return 'Feature-Flags synchronisieren';
        case AuditTrailAction.SyncLogSorter:
          return 'Stammsortierer synchronisieren';
        case AuditTrailAction.SyncMapStyle:
          return 'Kartenstile synchronisieren';
        case AuditTrailAction.SyncSetting:
          return 'Einstellungen synchronisieren';
        case AuditTrailAction.SyncVehicle:
          return 'Fahrzeuge synchronisieren';
        case AuditTrailAction.ProductGroupApiCreate:
          return 'Produktgruppe erstellen';
        case AuditTrailAction.ProductGroupApiUpdate:
          return 'Produktgruppe aktualisieren';
        case AuditTrailAction.ProductGroupApiDelete:
          return 'Produktgruppe löschen';
        case AuditTrailAction.ProductGroupSync:
          return 'Produktgruppe synchronisieren';
        case AuditTrailAction.DashboardCreate:
          return 'Dashboard erstellen';
        case AuditTrailAction.DashboardUpdate:
          return 'Dashboard aktualisieren';
        case AuditTrailAction.DashboardDelete:
          return 'Dashboard löschen';
        case AuditTrailAction.MoveTimberFromLogSorterBins:
          return 'Holz aus Stammsortierer-Fächern verschieben';
        case AuditTrailAction.ProductionOrderCreate:
          return 'Produktionsauftrag erstellen';
        case AuditTrailAction.ProductionOrderUpdate:
          return 'Produktionsauftrag aktualisieren';
        case AuditTrailAction.ProductionOrderDelete:
          return 'Produktionsauftrag löschen';
        case AuditTrailAction.ProductionOrderSync:
          return 'Produktionsauftrag synchronisieren';
        case AuditTrailAction.ProductionOrderChange:
          return 'Produktionsauftrag ändern';
      }
    },
    dashboardComponentType(type: DashboardComponentType): string {
      switch (type) {
        case DashboardComponentType.StorageAreaTable:
          return 'Lagerbereichstabelle';
        case DashboardComponentType.LatestReleaseNotes:
          return 'Neueste Release-Hinweise';
        case DashboardComponentType.ProductGroupDiagram:
          return 'Produktgruppendiagramm';
        case DashboardComponentType.ProductGroupTable:
          return 'Produktgruppentabelle';
        case DashboardComponentType.ProductTable:
          return 'Produkttabelle';
        case DashboardComponentType.AsljungapallenLagersaldo:
          return 'Asljungapallen Lagerbestand';
        default:
          return 'Unbekannter Komponententyp';
      }
    },
    keyboardAction(action: KeyboardActions): string {
      switch (action) {
        case KeyboardActions.LeaveInLogYard:
          return 'Ablegen';
        case KeyboardActions.PickFromLogYard:
          return 'Aufnehmen';
      }
    },
    productionOrderState(state: ProductionOrderState): string {
      switch (state) {
        case ProductionOrderState.Deleted:
          return 'Gelöscht';
        case ProductionOrderState.Planned:
          return 'Geplant';
        case ProductionOrderState.Released:
          return 'Freigegeben';
        case ProductionOrderState.Reserved:
          return 'Reserviert';
        case ProductionOrderState.Preparing:
          return 'Vorbereitung';
        case ProductionOrderState.Prepared:
          return 'Vorbereitet';
        case ProductionOrderState.Delivering:
          return 'Lieferung';
        case ProductionOrderState.Completed:
          return 'Abgeschlossen';
        case ProductionOrderState.Registered:
          return 'Registriert';
        case ProductionOrderState.PreparingPaused:
          return 'Vorbereitung pausiert';
        case ProductionOrderState.DeliveringPaused:
          return 'Lieferung pausiert';
      }

      return 'Unbekannt';
    },
  },
  reports: {
    exportFormat: {
      pdf: 'PDF',
      excel: 'Excel',
    },
    reportName(report: ReportType): string {
      switch (report) {
        case ReportType.LogYardStatus:
          return 'Statusbericht';
        case ReportType.LogYardPeriod:
          return 'Periodenbericht';
        default:
          return 'Unbekannter Bericht';
      }
    },
  },
  storageAreas: {
    actions: {
      create: {
        title: 'Lagerbereich erstellen',
        pending: 'Lagerbereich wird erstellt',
        error: 'Fehler beim Erstellen des Lagerbereichs',
      },
      fetch: {
        title: 'Lagerbereich wird abgerufen',
        error: 'Fehler beim Abrufen des Lagerbereichs',
      },
      fetchMany: {
        title: 'Lagerbereiche werden abgerufen',
        error: 'Fehler beim Abrufen der Lagerbereiche',
      },
      inventory: {
        title: 'Lagerbereich inventarisieren',
        error: 'Fehler beim Inventarisieren des Lagerbereichs',
      },
      undoInventory: {
        title: 'Inventar rückgängig machen',
        error: 'Fehler beim Rückgängigmachen des Inventars',
      },
      delete: {
        title: 'Lagerbereich wird gelöscht',
        error: 'Fehler beim Löschen des Lagerbereichs',
      },
      update: {
        title: 'Lagerbereich wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Lagerbereichs',
      },
      undoDelete: {
        title: 'Lagerbereich-Löschung rückgängig machen',
        error: 'Fehler beim Rückgängigmachen der Lagerbereich-Löschung',
      },
    },
    copy: 'Kopieren',
    notFound: 'Lagerbereich nicht gefunden',
    storageAreaTable: {
      firstMoveTo: 'Erste Bewegung',
      daysSinceFirstMove: 'Tage seit erster Bewegung',
    },
  },
  productionOrders: {
    actions: {
      fetchMany: {
        title: 'Produktionsaufträge werden abgerufen',
        error: 'Fehler beim Abrufen der Produktionsaufträge',
      },
      fetch: {
        title: 'Produktionsauftrag wird abgerufen',
        error: 'Fehler beim Abrufen des Produktionsauftrags',
      },
      create: {
        title: 'Produktionsauftrag wird erstellt',
        error: 'Fehler beim Erstellen des Produktionsauftrags',
      },
      update: {
        title: 'Produktionsauftrag wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Produktionsauftrags',
      },
      delete: {
        title: 'Produktionsauftrag wird gelöscht',
        error: 'Fehler beim Löschen des Produktionsauftrags',
        statusCodes: {
          409: 'Fehler beim Löschen des Produktionsauftrags',
        },
      },
      change: {
        title: 'Produktionsauftrag wird geändert',
        error: 'Fehler beim Ändern des Produktionsauftrags',
      },
    },
    listPage: {
      title: 'Produktionsaufträge',
      noFound: 'Kein Produktionsauftrag gefunden',
      loading: 'Produktionsaufträge werden geladen...',
    },
    deleteDialog: {
      header(name: string) {
        return `Produktionsauftrag ${name} löschen`;
      },
      description:
        'Sind Sie sicher, dass Sie diesen Produktionsauftrag löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Produktionsauftrag löschen',
    },
    editPage: {
      editTitle: 'Produktionsauftrag bearbeiten',
      createTitle: 'Produktionsauftrag erstellen',
      productionInstruction: 'Produktionsanweisung',
      vehicleInstruction: 'Fahrzeuganweisung',
      state: 'Status',
      recipeInstruction: 'Anweisungen',
      recipeMachineInstruction: 'Maschinenanweisungen',
      consumeEntireStock: 'Gesamten Bestand verbrauchen',
      noProducts: 'Keine Produkte',
      inProductsTitle: 'Eingangsprodukte',
      outProductsTitle: 'Ausgangsprodukte',
      editRecipeTitle: 'Rezept bearbeiten',
      addRecipeTitle: 'Rezept hinzufügen',
      addRecipeButton: 'Rezept hinzufügen',
      recipesTitle: 'Rezepte',
    },
    plannedStart: 'Geplanter Start',
  },
  shapes: {
    name: (id: string) => `Form #${id}`,
    multipleNames: (count: number) => `Bearbeite (${count}) Formen`,
    actions: {
      fetchMany: {
        title: 'Formen werden abgerufen',
        error: 'Fehler beim Abrufen der Formen',
      },
      fetch: {
        title: 'Form wird abgerufen',
        error: 'Fehler beim Abrufen der Form',
      },
      update: {
        title: 'Form wird aktualisiert',
        error: 'Fehler beim Aktualisieren der Form',
      },
      create: {
        title: 'Form erstellen',
        pending: 'Form wird erstellt',
        error: 'Fehler beim Erstellen der Form',
      },
      delete: {
        title: 'Form wird gelöscht',
        error: 'Fehler beim Löschen der Form',
      },
      query: {
        title: 'Formen werden abgefragt',
        error: 'Fehler beim Abfragen der Formen',
      },
      undoDelete: {
        title: 'Form-Löschung rückgängig machen',
        error: 'Fehler beim Rückgängigmachen der Form-Löschung',
      },
    },
    geofenceFieldSet: 'Geofence',
  },
  settings: {
    actions: {
      read: {
        title: 'Einstellungen lesen',
        error: 'Fehler beim Lesen der Einstellungen',
      },
      save: {
        title: 'Einstellungen speichern',
        error: 'Fehler beim Speichern der Einstellungen',
      },
    },
    general: {
      title: 'Allgemein',
      language: 'Sprache',
      dark: 'Dunkel',
      light: 'Hell',
      colorMode: 'Farbmodus',
      scale: 'Größe der Benutzeroberfläche',
    },
    map: {
      title: 'Karte',
      style: 'Stil',
      coordinate: 'Koordinaten',
      centerMapOnVehicle: 'Karte auf Fahrzeug zentrieren',
    },
    keybinds: {
      title: 'Tastenkombinationen',
    },
    about: {
      title: 'Über',
    },
    vehicle: {
      title: 'Fahrzeug',
      activeVehicle: 'Aktives Fahrzeug',
      speedLimit: 'Geschwindigkeitslimit 5 km/h zum Auslösen des Lagerbereichs',
    },
  },
  featureFlags: {
    actions: {
      read: {
        title: 'Feature-Flags lesen',
        error: 'Fehler beim Lesen der Feature-Flags',
      },
      refresh: {
        title: 'Feature-Flags aktualisieren',
        error: 'Fehler beim Aktualisieren der Feature-Flags',
      },
      toggle: {
        title: 'Feature-Flags umschalten',
        error: 'Fehler beim Umschalten der Feature-Flags',
      },
    },
    refresh: {
      success: 'Feature-Flags erfolgreich aktualisiert',
    },
    toggle: {
      success: 'Feature-Flag erfolgreich umgeschaltet',
    },
    component: {
      title: 'Feature-Flags',
      description: 'Funktionen in der Anwendung aktivieren oder deaktivieren',
      refreshButton: 'Feature-Flags aktualisieren',
    },
  },
  auth: {
    loggedOut: {
      title: 'Sie sind abgemeldet!',
      description: 'Sie müssen sich anmelden, um weiterarbeiten zu können.',
    },
    unauthorized: {
      title: 'Ihr Benutzer ist nicht berechtigt, diese App zu verwenden',
      description:
        'Sie müssen mit Ihrem Administrator sprechen, um Ihnen das Recht zur Nutzung zu geben.',
    },
  },
  map: {
    contextmenu: {
      create: {
        title: 'Erstellen',
        shape: 'Form',
        storageArea: 'Lagerbereich',
        logSorter: 'Stammsortierer',
        intake: 'Eingang',
      },
      edit: 'Bearbeiten',
      delete: 'Löschen',
      audit: 'Audit',
      copy: 'Kopieren',
      move: 'Verschieben',
    },
    actions: {
      saveMapMode: {
        title: 'Kartenmodus speichern',
        error: 'Fehler beim Speichern des Kartenmodus',
      },
    },
  },
  logSorter: {
    actions: {
      query: {
        title: 'Stammsortierer wird abgefragt',
        error: 'Fehler beim Abfragen des Stammsortierers',
      },
      update: {
        title: 'Stammsortierer wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Stammsortierers',
      },
      create: {
        title: 'Stammsortierer wird erstellt',
        error: 'Fehler beim Erstellen des Stammsortierers',
      },
      delete: {
        title: 'Stammsortierer wird gelöscht',
        error: 'Fehler beim Löschen des Stammsortierers',
      },
      fetch: {
        title: 'Stammsortierer wird abgerufen',
        error: 'Fehler beim Abrufen des Stammsortierers',
      },
    },
    drawer: {
      createTitle: 'Stammsortierer erstellen',
      binName: (binNumber: number) => `Fach ${binNumber}`,
      logSorterTab: 'Stammsortierer',
      binsTab: 'Fächer',
    },
    deleteConfirm: {
      title: `Stammsortierer löschen`,
      message: (name: string) =>
        `Sind Sie sicher, dass Sie den Stammsortierer ${name} löschen möchten?`,
    },
    panel: {
      identifier: 'Kennung',
      numberSequence: 'Zahlenfolge',
      numberOfBinsCol1: 'Anzahl der Fächer in Spalte eins',
      numberOfBinsCol2: 'Anzahl der Fächer in Spalte zwei',
      binLayout: 'Spalte zwei bis',
      binWidth: 'Fachbreite',
      binLength: 'Fachlänge',
      columnGap: 'Spaltenabstand',
      rowGap: 'Zeilenabstand',
      intakes: 'Eingänge',
      offsetY: 'Versetzt Y',
      offsetX: 'Versetzt X',
    },
  },
  mapStyles: {
    actions: {
      create: {
        title: 'Stil wird erstellt',
        error: 'Fehler beim Erstellen des Stils',
      },
      update: {
        title: 'Stil wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Stils',
      },
      delete: {
        title: 'Stil wird gelöscht',
        error: 'Fehler beim Löschen des Stils',
      },
      fetch: {
        title: 'Stil wird abgerufen',
        error: 'Fehler beim Abrufen des Stils',
      },
      fetchMany: {
        title: 'Stile werden abgerufen',
        error: 'Fehler beim Abrufen der Stile',
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Stil ${name} löschen`;
      },
      description:
        'Sind Sie sicher, dass Sie diesen Stil löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Stil löschen',
    },

    editDialog: {
      create: {
        title: 'Neuen Stil erstellen',
      },
      edit: {
        title: 'Stil bearbeiten',
      },
      tip: 'Sie können Strg+Leertaste drücken, um Informationen über verfügbare Funktionen anzuzeigen',
      documentation: 'Weitere Informationen zu den Formeln finden Sie in der ',
      documentationLinkText: 'Kartenstil-Dokumentation',
      shapeSet: 'Form',
      vehicleSet: 'Fahrzeug',
      labelFormula: 'Beschriftung',
      colorFormula: 'Farbe',
      outlineColorFormula: 'Umrissfarbe',
      outlineSizeFormula: 'Umrissgröße',
      fontSizeFormula: 'Schriftgröße',
      messageIconFormula: 'Nachrichtensymbol anzeigen',
      colorPicker: 'Farbwähler (Wird zum Anzeigen und Auswählen von Farbcodes verwendet)',
    },

    page: {
      title: 'Kartenstile',
      notFound: 'Keine Kartenstile gefunden',
      loading: 'Kartenstile werden geladen',
      copiedToClipboard: 'In Zwischenablage kopiert!',
      noStyleInClipboard: 'Kein Stil in der Zwischenablage',
      noStyleInClipboardDescription:
        'Sie müssen einen Stil in die Zwischenablage exportieren, bevor Sie ihn importieren können.',
      importFromClipboardTooltip: 'Aus Zwischenablage importieren',
    },
  },
  systemSettings: {
    page: {
      title: 'Systemeinstellungen',
      settingsSaved: 'Einstellungen erfolgreich gespeichert',
      storagearea: {
        title: 'Lagerbereich',
        nameFormat: 'Namensformat',
        nameFormatRequired: 'Namensformat ist erforderlich',
        nameFormatMissingSequence: 'Namensformat muss Platzhalter {#} für Sequenz enthalten',
        logYardLength: 'Holzplatzlänge',
        logYardWidth: 'Holzplatzbreite',
        logYardSideArea: 'Holzplatzseitenfläche',
        logYard: 'Holzplatz',
      },
      featureflags: {
        title: 'Feature-Flags',
      },
      reports: {
        title: 'Berichte',
      },
      map: {
        minZoom: 'Die minimale Zoomstufe der Karte (0-24)',
      },
      dashboards: {
        title: 'Dashboardcomponents',
      },
    },
  },
  actions: {
    fetchVersion: {
      title: 'Anwendungsversion wird abgerufen',
      error: 'Fehler beim Abrufen der Anwendungsversion',
    },
  },
  undo: {
    undoBtn: 'Rückgängig',
    pickTitle: 'Aufnahme rückgängig machen',
    getUndoPickDescription(
      oldStorageAreaCount: number,
      oldVehicleCount: number,
      storageAreaName: string,
    ): string {
      return `Bestätigen Sie, um ${oldVehicleCount} Stämme von Ihrem Fahrzeug zu entfernen und den Lagerbereich ${storageAreaName} auf die Menge ${oldStorageAreaCount} zurückzusetzen`;
    },
    placeTitle: 'Platzierung rückgängig machen',
    getUndoPlaceDescription(numberOfLogs: number, storageAreaName: string): string {
      return `Bestätigen Sie, um ${numberOfLogs} Stämme zu Ihrem Fahrzeug zurückzugeben und ${numberOfLogs} Stämme aus dem Lagerbereich ${storageAreaName} zu entfernen`;
    },
    inventoryTitle: 'Inventaraktion rückgängig machen',
    getUndoInventoryDescription(
      productname: string,
      numberOfLogs: number,
      storageAreaName: string,
    ): string {
      return `Bestätigen Sie, um die letzte Inventaraktion im Lagerbereich ${storageAreaName} rückgängig zu machen und die Menge von ${productname} auf ${numberOfLogs} zurückzusetzen`;
    },
    getUndoBulkInventoryDescription(
          productname: string,
          value: number,
          unit: Unit,
          storageAreaName: string,
        ): string {
          return `Bestätigen Sie, um die letzte inventuraktion im lagerbereich rückgängig zu machen ${storageAreaName} und festgelegten betrag ${productname} zurück zu ${value}`;
        },
    createStorageAreaTitle: 'Lagerbereich-Erstellung rückgängig machen',
    getUndoCreateStorageAreaDescription(storageAreaName: string): string {
      return `Bestätigen Sie, um die Erstellung des Lagerbereichs ${storageAreaName} rückgängig zu machen`;
    },
    deleteStorageAreaTitle: 'Lagerbereich-Löschung rückgängig machen',
    getUndoDeleteStorageAreaDescription(storageAreaName: string): string {
      return `Bestätigen Sie, um die Löschung des Lagerbereichs ${storageAreaName} rückgängig zu machen`;
    },
    editStorageAreaTitle: 'Lagerbereich-Bearbeitung rückgängig machen',
    getUndoEditStorageAreaDescription(storageAreaName: string): string {
      return `Bestätigen Sie, um die Bearbeitung des Lagerbereichs ${storageAreaName} rückgängig zu machen`;
    },
    createShape: {
      title: 'Form-Erstellung rückgängig machen',
      description: `Bestätigen Sie, um die Form-Erstellung rückgängig zu machen`,
    },
    updateShape: {
      title: 'Form-Aktualisierung rückgängig machen',
      description: 'Bestätigen Sie, um die Form-Aktualisierung rückgängig zu machen',
    },
    deleteShape: {
      title: 'Form-Löschung rückgängig machen',
      description: 'Bestätigen Sie, um die Form-Löschung rückgängig zu machen',
    },
  },
  quickMenu: {
    addStorageArea: 'Lagerbereich erstellen',
    editStorageArea: 'Lagerbereich bearbeiten',
  },
  moves: {
    actions: {
      moveTimberFromLogSorterBins: {
        title: 'Holz aus Stammsortierer-Fächern verschieben',
        error: 'Fehler beim Verschieben von Holz aus Stammsortierer-Fächern',
      },
      pick: {
        title: 'Aufnahme aus Lagerbereich',
        error: 'Fehler beim Aufnehmen aus Lagerbereich',
      },
      place: {
        title: 'Platzierung in Lagerbereich',
        error: 'Fehler beim Platzieren in Lagerbereich',
      },
      pickAndPlace: {
        title: 'Aufnehmen und platzieren',
        error: 'Fehler beim Aufnehmen und Platzieren',
      },
    },
    failedDialog: {
      title: 'Verschiebung fehlgeschlagen!',
      description: (fromStorageArea: string, toStorageArea: string) =>
        `Verschiebung von ${fromStorageArea} nach ${toStorageArea}`,
    },
    moveTimberFromLogSorterBins: 'Holz aus Fächern verschieben',
  },
  home: {
    welcome: 'Willkommen bei',
    description: 'Nächste Generation des Logistiksystems',
    username: 'Benutzername',
    enterUsername: 'Geben Sie Ihren Benutzernamen ein, um sich anzumelden',
    rememberUsername: 'Benutzernamen merken',
    signInButton: 'Anmelden',
  },
  unsavedChanges: {
    title: 'Sie haben ungespeicherte Änderungen!',
    description: 'Was möchten Sie damit tun?',
  },
  audit: {
    dialog: {
      title: (name: string) => `Änderungen von ${name}`,
    },
    actions: {
      fetchShape: {
        title: 'Form-Audit wird abgerufen',
        error: 'Fehler beim Abrufen des Form-Audits',
      },
      fetchVehicle: {
        title: 'Fahrzeug-Audit wird abgerufen',
        error: 'Fehler beim Abrufen des Fahrzeug-Audits',
      },
      fetchProduct: {
        title: 'Produkt-Audit wird abgerufen',
        error: 'Fehler beim Abrufen des Produkt-Audits',
      },
      fetchProductGroup: {
        title: 'Produktgruppen-Audit wird abgerufen',
        error: 'Fehler beim Abrufen des Produktgruppen-Audits',
      },
      fetchDashboard: {
        title: 'Dashboard-Audit wird abgerufen',
        error: 'Fehler beim Abrufen des Dashboard-Audits',
      },
      fetchProductionOrder: {
        title: 'Produktionsauftrags-Audit wird abgerufen',
        error: 'Fehler beim Abrufen des Produktionsauftrags-Audits',
      },
    },
  },
  units: {
    millimeters: 'mm',
    cubicMeter: 'm³',
    kilogram: 'kg',
    ton: 'ton',
    kgPerM3: 'kg/m3',
  },
  coils: {
    actions: {
      fetchMany: {
        title: 'Coils werden abgerufen',
        error: 'Fehler beim Abrufen der Coils',
      },
      fetch: {
        title: 'Coil wird abgerufen',
        error: 'Fehler beim Abrufen des Coils',
      },
      create: {
        title: 'Coil wird erstellt',
        error: 'Fehler beim Erstellen des Coils',
      },
      update: {
        title: 'Coil wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Coils',
      },
      delete: {
        title: 'Coil wird gelöscht',
        error: 'Fehler beim Löschen des Coils',
      },
      undoMove: {
        title: 'Coil-Verschiebung rückgängig machen',
        error: 'Fehler beim Rückgängigmachen der Coil-Verschiebung',
      },
    },

    page: {
      title: 'Coils',
      notFound: 'Keine Coils gefunden',
      loading: 'Coils werden geladen',
    },

    deleteDialog: {
      header(name: string) {
        return `Coil ${name} löschen`;
      },
      description:
        'Sind Sie sicher, dass Sie dieses Coil löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Coil löschen',
    },

    editDialog: {
      create: {
        title: 'Neues Coil erstellen',
      },
      edit: {
        title: 'Coil bearbeiten',
      },
    },

    slots: 'Anzahl der Plätze',
    slotWidth: 'Platzbreite',
    maxHeight: 'Max. Höhe',
    maxLevels: 'Max. Ebenen',
    diameter: 'Durchmesser',
    depth: 'Tiefe',
    product: 'Produkt',
    storageArea: 'Lagerbereich',
    position: 'Position',
    level: 'Ebene',
    maxCoilDiameter: 'Max. Coil-Durchmesser',

    moveDialog: {
      title: 'Coil verschieben',
    },

    unableToDrawCoils: (coils: string[]) => {
      if (coils.length === 1) {
        return `${coils[0]} befindet sich an einer ungültigen Position im Lagerbereich`;
      } else {
        return 'Viele Coils haben ungültige Positionen im Lagerbereich';
      }
    },
  },
  logYard: {
    moveDialog: {
      title: 'Stämme verschieben',
    },
  },
  bulk: {
  actions: {
    fetch: {
      title: 'Bulk wird geladen',
      error: 'Fehler beim Laden von Bulk',
    },
    create: {
      title: 'Bulk wird erstellt',
      error: 'Fehler beim Erstellen von Bulk',
    },
    update: {
      title: 'Bulk wird aktualisiert',
      error: 'Fehler beim Aktualisieren von Bulk',
    },
    delete: {
      title: 'Bulk wird gelöscht',
      error: 'Fehler beim Löschen von Bulk',
    },
    undoMove: {
      title: 'Bulk-Verschiebung rückgängig machen',
      error: 'Fehler beim Rückgängigmachen der Bulk-Verschiebung',
    },
  },
  maxWeight: 'Maximales Gewicht',
  maxVolume: 'Maximales Volumen',

  bulkUnit(value?: Unit | null): string {
    switch (value) {
      case Unit.Kilogram:
        return 'Kilogramm';
      case Unit.CubicMeter:
        return 'Kubikmeter';
      case Unit.LongTon:
        return 'Long Tons';
      case Unit.MetricTon:
        return 'Tonnen';
      case Unit.Pound:
        return 'Pfund';
      case Unit.ShortTon:
        return 'Short Tons';
      default:
        return '';
    }
  },
  bulkUnitShort(value?: Unit | null): string {
    switch (value) {
      case Unit.MetricTon:
        return 't';
      case Unit.Kilogram:
        return 'kg';
      case Unit.CubicMeter:
        return 'm³';
      case Unit.LongTon:
        return 'l.tn';
      case Unit.Pound:
        return 'lb';
      case Unit.ShortTon:
        return 'tn';
      default:
        return '';
    }
  },
  moveDialog: {
    title: 'Bulk verschieben',
  },
},
  dashboard: {
    actions: {
      fetchMany: {
        title: 'Dashboards werden abgerufen',
        error: 'Fehler beim Abrufen der Dashboards',
      },
      fetch: {
        title: 'Dashboard wird abgerufen',
        error: 'Fehler beim Abrufen des Dashboards',
      },
      create: {
        title: 'Dashboard wird erstellt',
        error: 'Fehler beim Erstellen des Dashboards',
      },
      update: {
        title: 'Dashboard wird aktualisiert',
        error: 'Fehler beim Aktualisieren des Dashboards',
      },
      delete: {
        title: 'Dashboard wird gelöscht',
        error: 'Fehler beim Löschen des Dashboards',
        statusCodes: {
          409: 'Fehler beim Löschen des Dashboards',
        },
      },
    },
    listPage: {
      title: 'Dashboards',
      noFound: 'Kein Dashboard gefunden',
      loading: 'Dashboards werden geladen...',
    },
    deleteDialog: {
      header(name: string) {
        return `Dashboard ${name} löschen`;
      },
      description:
        'Sind Sie sicher, dass Sie dieses Dashboard löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      deleteButton: 'Ja, Dashboard löschen',
    },
    editPage: {
      editTitle: 'Dashboard bearbeiten',
      createTitle: 'Dashboard erstellen',
    },
    editComponentDialog: {
      editTitle: 'Komponente bearbeiten',
      addTitle: 'Komponente hinzufügen',
    },
    productGroupTable: {
      title: 'Bestand pro Produktgruppe',
    },
    productGroupDiagram: {
      title: 'Bestand pro Produktgruppe',
    },
    productTable: {
      title: 'Bestand pro Produkt',
    },
  },
  overview: {
    title: 'Übersicht',
  },
  releaseNotes: {
    actions: {
      fetchLatest: {
        title: 'Neueste Release-Hinweise werden abgerufen',
        error: 'Fehler beim Abrufen der neuesten Release-Hinweise',
      },
      fetchMany: {
        title: 'Release-Hinweise werden abgerufen',
        error: 'Fehler beim Abrufen der Release-Hinweise',
      },
    },
  },
  keybinds: {
    action: 'Aktion',
    keybind: 'Tastenkombination',
  },
  reportPage: {
    selectReport: 'Bericht auswählen',
    showReport: 'Bericht anzeigen',
    selectExportFormat: 'Exportformat auswählen',
    exportReport: 'Bericht exportieren',
    exportError: 'Fehler beim Exportieren des Berichts',
    showError: 'Fehler beim Anzeigen des Berichts',
  },
};
